import { ChangeEvent, forwardRef, KeyboardEvent, useImperativeHandle, useRef } from 'react';
import cn from 'classnames';
import send from '@packages/shared/assets/chat/send-message.svg';
import TextareaAutosize from '@packages/shared/core/common/components/common/TextareaAutosize';
import { isEnterKeyPressed } from '@packages/shared/core/common/utils/events';
import Image from '@packages/shared/core/common/components/common/Image';
import IconButton from '@packages/shared/core/common/components/common/IconButton';
import { DataTestId } from '@packages/shared/core/common/constants/dataTestId';
import styles from './ChatInput.module.scss';

type ChatInputProps = {
  onSubmit: (value: string) => void;
  onChange: (value: string) => void;
  value: string;
  className?: string;
  disabled?: boolean;
};

export type ChatInputRefProps = {
  focus: () => void;
};

const ChatInput = forwardRef<ChatInputRefProps, ChatInputProps>((props: ChatInputProps, ref) => {
  const { onSubmit, value, onChange, className, disabled } = props;
  const maxTextareaRows = 5;

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useImperativeHandle(ref, () => ({
    focus: focusInput,
  }));

  const handleSubmit = () => {
    if (value.trim() !== '') {
      onSubmit(value);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEnterKeyPressed(e) && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const buttonSendClasses = cn({
    [styles.buttonSend]: true,
    [styles.buttonSendDisabled]: !value,
  });

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.inputContainer}>
        <TextareaAutosize
          disabled={disabled}
          ref={inputRef}
          placeholder="Type your text here"
          className={styles.input}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          maxRows={maxTextareaRows}
          value={value}
          data-testid={DataTestId.CHAT_MESSAGE_FIELD}
        />
      </div>

      <div className={styles.buttonSendWrapper}>
        <IconButton onClick={handleSubmit} className={buttonSendClasses} data-testid={DataTestId.SEND_MESSAGE_BUTTON}>
          <Image type="future" src={send} alt="send message" width={24} height={24} />
        </IconButton>
      </div>
    </div>
  );
});

export default ChatInput;
